import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import LockedFeature from '../components/LockedFeature';
import ReportCardPreviewModal from '../components/ReportCardPreviewModal';
import { useAuth } from '../context/AuthContext';
import gradesService from '../services/gradesService';
import schoolsService from '../services/schoolsService';
import classesService from '../services/classesService';
import studentsService from '../services/studentsService';
import curriculumService from '../services/curriculumService';
import { Plus, X, BookOpen, Student, MagnifyingGlass, FilePlus, ChartBar, Eye, Spinner, Target, ArrowUUpLeft } from '@phosphor-icons/react';

const MASTERY_LEVELS = [
  { value: 'not_yet', label: 'Not Yet' },
  { value: 'approaching', label: 'Approaching' },
  { value: 'meets', label: 'Meets' },
  { value: 'exceeds', label: 'Exceeds' },
];

// GES Grading Scale Reference
const GES_SCALE = [
  { grade: '1', min: 80, max: 100, desc: 'Excellent', color: '#059669' },
  { grade: '2', min: 70, max: 79, desc: 'Very Good', color: '#10B981' },
  { grade: '3', min: 60, max: 69, desc: 'Good', color: '#34D399' },
  { grade: '4', min: 55, max: 59, desc: 'Credit', color: '#FBBF24' },
  { grade: '5', min: 50, max: 54, desc: 'Pass', color: '#F59E0B' },
  { grade: '6', min: 45, max: 49, desc: 'Weak Pass', color: '#F97316' },
  { grade: '7', min: 40, max: 44, desc: 'Very Weak', color: '#EF4444' },
  { grade: '8', min: 35, max: 39, desc: 'Poor', color: '#DC2626' },
  { grade: '9', min: 0, max: 34, desc: 'Fail', color: '#991B1B' },
];

function getGradeInfo(percentage) {
  for (const g of GES_SCALE) {
    if (percentage >= g.min && percentage <= g.max) {
      return g;
    }
  }
  return GES_SCALE[GES_SCALE.length - 1];
}

function GradesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('class'); // 'class', 'student', 'enter'
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedAssessment, setSelectedAssessment] = useState('');
  const [classGrades, setClassGrades] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [showReportCardModal, setShowReportCardModal] = useState(false);
  const [showBulkReportModal, setShowBulkReportModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHTML, setPreviewHTML] = useState('');
  const [gradeEntries, setGradeEntries] = useState([]);
  
  // Single report card state
  const [reportCardStudent, setReportCardStudent] = useState('');
  const [reportCardStudentName, setReportCardStudentName] = useState('');
  const [reportCardTerm, setReportCardTerm] = useState('');
  const [reportCardStatus, setReportCardStatus] = useState(null);
  const [reportCardData, setReportCardData] = useState(null);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  
  // Bulk report card state
  const [bulkReportMode, setBulkReportMode] = useState(false); // true when viewing bulk reports
  const [bulkReportClass, setBulkReportClass] = useState('');
  const [bulkReportTerm, setBulkReportTerm] = useState('');
  const [bulkReportStudents, setBulkReportStudents] = useState([]);
  const [currentReportIndex, setCurrentReportIndex] = useState(0);
  const [bulkDownloadingPDF, setBulkDownloadingPDF] = useState(false);
  
  const [newSubject, setNewSubject] = useState({
    name: '',
    code: '',
    category: 'core',
    credit_hours: 2
  });
  
  const [bulkGradeData, setBulkGradeData] = useState({
    subject_id: '',
    assessment_type: 'class_work',
    max_score: 100,
    academic_term_id: ''
  });

  // Standards mastery entry — parallel/optional to the numeric grade entry above
  const [standards, setStandards] = useState([]);
  const [masteryStudents, setMasteryStudents] = useState([]);
  const [masteryForm, setMasteryForm] = useState({
    student_id: '', class_id: '', subject_id: '', standard_id: '',
    academic_term_id: '', mastery_level: 'approaching', notes: ''
  });

  // Recalled report cards — admin-only view of what's been called back for correction
  const [recalls, setRecalls] = useState([]);
  const [loadingRecalls, setLoadingRecalls] = useState(false);
  const [recallsFilter, setRecallsFilter] = useState('outstanding'); // 'outstanding' | 'resolved' | 'all'
  const [savingMastery, setSavingMastery] = useState(false);
  const [masteryStudentFilter, setMasteryStudentFilter] = useState('');
  const [masteryRecords, setMasteryRecords] = useState([]);
  const [loadingMasteryRecords, setLoadingMasteryRecords] = useState(false);

  // Dialog Modal
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });

  // Dialog helpers
  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  const assessmentTypes = [
    { value: 'class_work', label: 'Class Work' },
    { value: 'homework', label: 'Homework' },
    { value: 'quiz', label: 'Quiz' },
    { value: 'mid_term', label: 'Mid-Term Exam' },
    { value: 'end_of_term', label: 'End of Term Exam' },
    { value: 'project', label: 'Project' },
  ];

  // Load classes
  useEffect(() => {
    classesService.listClasses()
      .then(data => setClasses(data || []))
      .catch(err => console.error(err));
  }, []);

  // Load subjects
  useEffect(() => {
    gradesService.listSubjects()
      .then(data => setSubjects(data || []))
      .catch(err => console.error(err));
  }, []);

  // Load curriculum standards catalog (for the Standards Mastery tab)
  useEffect(() => {
    curriculumService.standards()
      .then(data => setStandards(data || []))
      .catch(err => console.error(err));
  }, []);

  // Load students for the class selected in the mastery entry form
  useEffect(() => {
    if (!masteryForm.class_id) { setMasteryStudents([]); return; }
    studentsService.listStudents({ class_id: masteryForm.class_id })
      .then(data => setMasteryStudents(data.items || []))
      .catch(() => toast.error('Failed to load students for this class'));
  }, [masteryForm.class_id]);

  // Load mastery records for the student selected in the mastery filter
  useEffect(() => {
    if (!masteryStudentFilter) { setMasteryRecords([]); return; }
    setLoadingMasteryRecords(true);
    gradesService.getStudentMasteryRecords(masteryStudentFilter)
      .then(data => setMasteryRecords(data || []))
      .catch(() => toast.error('Failed to load mastery records'))
      .finally(() => setLoadingMasteryRecords(false));
  }, [masteryStudentFilter]);

  const handleSubmitMasteryRecord = async (e) => {
    e.preventDefault();
    if (!masteryForm.student_id || !masteryForm.standard_id || !masteryForm.class_id || !masteryForm.subject_id || !masteryForm.academic_term_id) {
      toast.error('Please fill in student, class, subject, term, and standard');
      return;
    }
    setSavingMastery(true);
    try {
      await gradesService.createMasteryRecord(masteryForm);
      toast.success('Mastery assessment recorded');
      setMasteryForm(f => ({ ...f, standard_id: '', notes: '' }));
      if (masteryStudentFilter === masteryForm.student_id) {
        const data = await gradesService.getStudentMasteryRecords(masteryStudentFilter);
        setMasteryRecords(data || []);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to record mastery assessment');
    } finally {
      setSavingMastery(false);
    }
  };

  // Load class grades when selection changes
  const loadClassGrades = useCallback(() => {
    if (!selectedClass) return;
    setLoading(true);

    const params = {};
    if (selectedSubject) params.subject_id = selectedSubject;
    if (selectedAssessment) params.assessment_type = selectedAssessment;

    gradesService.getClassGrades(selectedClass, params)
      .then(data => setClassGrades(data))
      .catch(err => toast.error('Failed to load grades'))
      .finally(() => setLoading(false));
  }, [selectedClass, selectedSubject, selectedAssessment]);

  useEffect(() => {
    if (selectedClass && view === 'class') {
      loadClassGrades();
    }
  }, [selectedClass, selectedSubject, selectedAssessment, view, loadClassGrades]);

  // Load students for grade entry
  const loadStudentsForGradeEntry = useCallback(() => {
    if (!selectedClass) return;
    studentsService.listStudents({ class_id: selectedClass })
      .then(data => {
        const studentList = data.items || [];
        setStudents(studentList);
        // Initialize grade entries
        setGradeEntries(studentList.map(s => ({
          student_id: s.id,
          student_name: s.full_name,
          score: ''
        })));
      })
      .catch(err => toast.error('Failed to load students'));
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && view === 'enter') {
      loadStudentsForGradeEntry();
    }
  }, [selectedClass, view, loadStudentsForGradeEntry]);

  // Load academic terms from backend
  const loadAcademicTerms = useCallback(async () => {
    if (!user || !user.school_id) {
      toast.error('School information not available');
      return;
    }
    
    try {
      const terms = await schoolsService.listTerms(user.school_id);
      setAcademicTerms(terms || []);
      
      // Set first term or current term as default
      if (terms && terms.length > 0) {
        const currentTerm = terms.find(t => t.is_current);
        setReportCardTerm(currentTerm ? currentTerm.id : terms[0].id);
      }
    } catch (error) {
      toast.error('Failed to load academic terms');
    }
  }, [user]);

  // Load students and academic terms when grade entry modal opens
  useEffect(() => {
    if (showGradeModal && selectedClass) {
      loadStudentsForGradeEntry();
      loadAcademicTerms();
    }
  }, [showGradeModal, selectedClass, loadStudentsForGradeEntry, loadAcademicTerms]);

  // Seed default subjects
  const seedSubjects = async () => {
    try {
      const result = await gradesService.seedDefaultSubjects();
      toast.success(result.message);
      // Reload subjects
      const data = await gradesService.listSubjects();
      setSubjects(data || []);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Create new subject
  const handleCreateSubject = async (e) => {
    e.preventDefault();
    try {
      await gradesService.createSubject(newSubject);
      toast.success('Subject created successfully!');
      setShowSubjectModal(false);
      setNewSubject({ name: '', code: '', category: 'core', credit_hours: 2 });
      const data = await gradesService.listSubjects();
      setSubjects(data || []);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Submit bulk grades
  const handleSubmitGrades = async () => {
    if (!bulkGradeData.subject_id) {
      toast.error('Please select a subject');
      return;
    }
    if (!bulkGradeData.academic_term_id) {
      toast.error('Please select an academic term');
      return;
    }
    
    
    const validEntries = gradeEntries.filter(e => e.score !== '' && e.score !== null);
    if (validEntries.length === 0) {
      showDialog({
        title: 'No Grades Entered',
        message: 'Please enter at least one grade before submitting.',
        type: 'warning',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
      return;
    }
    
    const grades = validEntries.map(entry => ({
      student_id: entry.student_id,
      class_id: selectedClass,
      subject_id: bulkGradeData.subject_id,
      academic_term_id: bulkGradeData.academic_term_id,
      assessment_type: bulkGradeData.assessment_type,
      score: parseFloat(entry.score),
      max_score: parseFloat(bulkGradeData.max_score)
    }));
    
    try {
      const result = await gradesService.bulkSubmitGrades(grades);
      toast.success(result.message);
      setShowGradeModal(false);
      loadClassGrades();
    } catch (error) {
      showDialog({
        title: 'Grade Submission Failed',
        message: error.message || 'Failed to submit grades. Please verify the data and try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    }
  };

  // Load academic terms from backend (for report card modal)
  useEffect(() => {
    if (showReportCardModal) {
      loadAcademicTerms();
    }
  }, [showReportCardModal, loadAcademicTerms]);

  // Load academic terms for the Standards Mastery tab too
  useEffect(() => {
    if (view === 'mastery') {
      loadAcademicTerms();
    }
  }, [view, loadAcademicTerms]);

  const loadRecalls = useCallback(async () => {
    setLoadingRecalls(true);
    try {
      const resolvedParam = recallsFilter === 'all' ? undefined : recallsFilter === 'resolved';
      const data = await gradesService.listReportCardRecalls(resolvedParam);
      setRecalls(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load recalled report cards');
    } finally {
      setLoadingRecalls(false);
    }
  }, [recallsFilter]);

  useEffect(() => {
    if (view === 'recalls') {
      loadRecalls();
    }
  }, [view, recallsFilter, loadRecalls]);

  // Re-fetch the current preview — used both for the initial open and to
  // refresh html/status/remarks after saving report card details.
  const refreshReportCardPreview = useCallback(async () => {
    const data = await gradesService.previewReportCard(reportCardStudent, reportCardTerm);
    setPreviewHTML(data.html);
    setReportCardStudentName(data.student_name);
    setReportCardStatus(data.status || null);
    setReportCardData(data.report_data || null);
    return data;
  }, [reportCardStudent, reportCardTerm]);

  // Download report card as PDF
  // Preview report card as HTML modal
  const handlePreviewReportCard = async () => {
    if (!reportCardStudent) {
      toast.error('Please select a student');
      return;
    }

    if (!reportCardTerm) {
      toast.error('Please select an academic term');
      return;
    }

    setDownloadingPDF(true);
    try {
      await refreshReportCardPreview();
      setShowPreviewModal(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Open a recalled report's preview directly from the Recalled Reports list —
  // sets the same state the "Select Report Card" flow uses, so Approve/Recall
  // and the correction-history panel in the modal work identically.
  const handleViewRecalledReport = async (recall) => {
    setReportCardStudent(recall.student_id);
    setReportCardTerm(recall.academic_term_id);
    setDownloadingPDF(true);
    try {
      const data = await gradesService.previewReportCard(recall.student_id, recall.academic_term_id);
      setPreviewHTML(data.html);
      setReportCardStudentName(data.student_name);
      setReportCardStatus(data.status || null);
      setReportCardData(data.report_data || null);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to load report card');
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Update grade entry
  const updateGradeEntry = (studentId, score) => {
    setGradeEntries(prev => prev.map(e => 
      e.student_id === studentId ? { ...e, score } : e
    ));
  };

  // Load academic terms when bulk report modal opens
  useEffect(() => {
    if (showBulkReportModal) {
      loadAcademicTerms();
    }
  }, [showBulkReportModal, loadAcademicTerms]);

  // Load bulk report cards for entire class
  const handleLoadBulkReports = async () => {
    if (!bulkReportClass) {
      toast.error('Please select a class');
      return;
    }
    
    if (!bulkReportTerm) {
      toast.error('Please select an academic term');
      return;
    }
    
    setBulkDownloadingPDF(true);
    try {
      const data = await gradesService.bulkPreviewReportCards(bulkReportClass, bulkReportTerm);
      
      if (data.students && data.students.length > 0) {
        setBulkReportStudents(data.students);
        setCurrentReportIndex(0);
        setBulkReportMode(true);
        setPreviewHTML(data.students[0].html);
        setReportCardStudentName(data.students[0].student_name);
        setShowBulkReportModal(false);
        setShowPreviewModal(true);
      } else {
        toast.error('No students found for this class or no grades recorded');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load bulk reports');
    } finally {
      setBulkDownloadingPDF(false);
    }
  };

  // Navigate to next student report in bulk view
  const goToNextReport = () => {
    if (currentReportIndex < bulkReportStudents.length - 1) {
      const nextIndex = currentReportIndex + 1;
      setCurrentReportIndex(nextIndex);
      setPreviewHTML(bulkReportStudents[nextIndex].html);
      setReportCardStudentName(bulkReportStudents[nextIndex].student_name);
    }
  };

  // Navigate to previous student report in bulk view
  const goToPreviousReport = () => {
    if (currentReportIndex > 0) {
      const prevIndex = currentReportIndex - 1;
      setCurrentReportIndex(prevIndex);
      setPreviewHTML(bulkReportStudents[prevIndex].html);
      setReportCardStudentName(bulkReportStudents[prevIndex].student_name);
    }
  };

  // Jump to specific student in bulk view
  const jumpToStudent = (index) => {
    if (index >= 0 && index < bulkReportStudents.length) {
      setCurrentReportIndex(index);
      setPreviewHTML(bulkReportStudents[index].html);
      setReportCardStudentName(bulkReportStudents[index].student_name);
    }
  };

  // Download all reports for the class
  const handleDownloadAllReports = async () => {
    if (bulkReportStudents.length === 0) {
      toast.error('No reports to download');
      return;
    }
    
    try {
      // Prepare all reports for bulk download
      const reportPayload = {
        class_id: bulkReportClass,
        term_id: bulkReportTerm,
        format: 'zip' // Backend should return ZIP of all PDFs
      };
      
      const response = await gradesService.bulkDownloadReportCards(reportPayload);
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `class-report-cards-${bulkReportClass}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${bulkReportStudents.length} report cards`);
    } catch (error) {
      toast.error(error.message || 'Failed to download reports');
    }
  };

  // Approve every draft report card in the class/term being bulk-previewed
  const handleApproveAllReports = async () => {
    try {
      const result = await gradesService.bulkApproveReportCards(bulkReportClass, bulkReportTerm);
      toast.success(result.message || `Approved ${result.approved_count} report card(s)`);
      const data = await gradesService.bulkPreviewReportCards(bulkReportClass, bulkReportTerm);
      if (data.students) {
        setBulkReportStudents(data.students);
        setPreviewHTML(data.students[currentReportIndex]?.html || data.students[0]?.html);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to approve report cards');
      throw error;
    }
  };

  return (
    <Layout>
      <div className="page" data-testid="grades-page">
        <div className="page-header">
          <div>
            <h1>Grades & Assessment</h1>
            <p>Record and manage student grades using GES grading scale</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowSubjectModal(true)}
              data-testid="add-subject-btn"
            >
              <Plus size={18} /> Add Subject
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => setShowGradeModal(true)}
              disabled={!selectedClass}
              data-testid="enter-grades-btn"
            >
              <FilePlus size={18} /> Enter Grades
            </button>
            <LockedFeature module="academic_reports">
              <button
                className="btn btn-secondary"
                onClick={() => setShowReportCardModal(true)}
                data-testid="download-reportcard-btn"
              >
                <Eye size={18} /> Select Report Card
              </button>
            </LockedFeature>
            <LockedFeature module="academic_reports">
              <button
                className="btn btn-primary"
                onClick={() => setShowBulkReportModal(true)}
                disabled={!selectedClass}
                data-testid="bulk-reportcard-btn"
              >
                <ChartBar size={18} /> Class Report Cards
              </button>
            </LockedFeature>
            {(user?.role === 'super_admin' || user?.role === 'school_admin') && (
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/grades/report-templates')}
              >
                Report Templates
              </button>
            )}
          </div>
        </div>

        {/* View Tabs */}
        <div className="tabs-container">
          <button 
            className={`tab-btn ${view === 'class' ? 'active' : ''}`}
            onClick={() => setView('class')}
          >
            <ChartBar size={18} /> Class Overview
          </button>
          <button 
            className={`tab-btn ${view === 'scale' ? 'active' : ''}`}
            onClick={() => setView('scale')}
          >
            <BookOpen size={18} /> GES Grade Scale
          </button>
          <button
            className={`tab-btn ${view === 'subjects' ? 'active' : ''}`}
            onClick={() => setView('subjects')}
          >
            <Student size={18} /> Subjects
          </button>
          <button
            className={`tab-btn ${view === 'mastery' ? 'active' : ''}`}
            onClick={() => setView('mastery')}
          >
            <Target size={18} /> Standards Mastery
          </button>
          {(user?.role === 'super_admin' || user?.role === 'school_admin') && (
            <button
              className={`tab-btn ${view === 'recalls' ? 'active' : ''}`}
              onClick={() => setView('recalls')}
            >
              <ArrowUUpLeft size={18} /> Recalled Reports
            </button>
          )}
        </div>

        {/* Class Overview View */}
        {view === 'class' && (
          <div className="grades-content">
            <div className="filters-row">
              <div className="form-group">
                <label>Select Class</label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  data-testid="class-select"
                >
                  <option value="">Choose a class...</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Filter by Subject</label>
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                >
                  <option value="">All Subjects</option>
                  {subjects.map(subj => (
                    <option key={subj.id} value={subj.id}>{subj.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Filter by Assessment</label>
                <select
                  value={selectedAssessment}
                  onChange={(e) => setSelectedAssessment(e.target.value)}
                >
                  <option value="">All Assessments</option>
                  {assessmentTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading grades...</div>
            ) : classGrades ? (
              <div className="grades-table-container">
                <div className="class-summary">
                  <h3>{classGrades.class_name}</h3>
                  <span className="student-count">{classGrades.student_count} students</span>
                </div>
                
                <div className="table-container">
                  <table className="data-table grades-table" data-testid="grades-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>ID</th>
                        <th>Grades Recorded</th>
                        <th>Average</th>
                        <th>Grade</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classGrades.students.map(student => {
                        const gradeInfo = getGradeInfo(student.average_percentage);
                        return (
                          <tr key={student.student_id} data-testid={`grade-row-${student.student_id}`}>
                            <td className="student-name">{student.student_name}</td>
                            <td className="mono">{student.student_number}</td>
                            <td>{student.grades.length}</td>
                            <td>
                              <span className="percentage-badge" style={{ 
                                backgroundColor: `${gradeInfo.color}20`,
                                color: gradeInfo.color 
                              }}>
                                {student.average_percentage}%
                              </span>
                            </td>
                            <td>
                              <span className="grade-badge" style={{ backgroundColor: gradeInfo.color }}>
                                {student.letter_grade}
                              </span>
                            </td>
                            <td>{student.grade_description}</td>
                          </tr>
                        );
                      })}
                      {classGrades.students.length === 0 && (
                        <tr>
                          <td colSpan="6" className="empty-state">No grades recorded yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="empty-state-card">
                <ChartBar size={48} weight="duotone" />
                <h3>Select a Class</h3>
                <p>Choose a class from the dropdown to view student grades</p>
              </div>
            )}
          </div>
        )}

        {/* GES Grade Scale View */}
        {view === 'scale' && (
          <div className="grade-scale-content">
            <div className="scale-header">
              <h2>Ghana Education Service (GES) Grading Scale</h2>
              <p>Standard grading scale used in Ghanaian Basic and JHS schools</p>
            </div>
            <div className="scale-grid">
              {GES_SCALE.map((grade, index) => (
                <div key={index} className="scale-card" style={{ borderLeftColor: grade.color }}>
                  <div className="scale-grade" style={{ backgroundColor: grade.color }}>
                    {grade.grade}
                  </div>
                  <div className="scale-info">
                    <div className="scale-range">{grade.min} - {grade.max}%</div>
                    <div className="scale-desc">{grade.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subjects View */}
        {view === 'subjects' && (
          <div className="subjects-content">
            {subjects.length === 0 ? (
              <div className="empty-state-card">
                <BookOpen size={48} weight="duotone" />
                <h3>No Subjects Created</h3>
                <p>Create subjects to start recording grades</p>
                <div className="empty-actions">
                  <button className="btn btn-primary" onClick={seedSubjects}>
                    Load Default GES Subjects
                  </button>
                  <button className="btn btn-secondary" onClick={() => setShowSubjectModal(true)}>
                    Add Custom Subject
                  </button>
                </div>
              </div>
            ) : (
              <div className="subjects-grid">
                {subjects.map(subject => (
                  <div key={subject.id} className="subject-card" data-testid={`subject-${subject.id}`}>
                    <div className="subject-code">{subject.code}</div>
                    <div className="subject-name">{subject.name}</div>
                    <div className="subject-meta">
                      <span className={`badge ${subject.category}`}>{subject.category}</span>
                      <span>{subject.credit_hours} credit hrs</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Standards Mastery View */}
        {view === 'mastery' && (
          <div className="grades-content">
            <div className="filters-row">
              <div className="form-group">
                <h3 style={{ margin: 0 }}>Record a mastery assessment</h3>
              </div>
            </div>
            <form onSubmit={handleSubmitMasteryRecord} className="modal-form" style={{ maxWidth: 640 }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Class</label>
                  <select
                    value={masteryForm.class_id}
                    onChange={(e) => setMasteryForm({ ...masteryForm, class_id: e.target.value, student_id: '' })}
                  >
                    <option value="">Select class...</option>
                    {classes.map(cls => <option key={cls.id} value={cls.id}>{cls.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Student</label>
                  <select
                    value={masteryForm.student_id}
                    onChange={(e) => setMasteryForm({ ...masteryForm, student_id: e.target.value })}
                    disabled={!masteryForm.class_id}
                  >
                    <option value="">Select student...</option>
                    {masteryStudents.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Subject</label>
                  <select
                    value={masteryForm.subject_id}
                    onChange={(e) => setMasteryForm({ ...masteryForm, subject_id: e.target.value })}
                  >
                    <option value="">Select subject...</option>
                    {subjects.map(subj => <option key={subj.id} value={subj.id}>{subj.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Academic Term</label>
                  <select
                    value={masteryForm.academic_term_id}
                    onChange={(e) => setMasteryForm({ ...masteryForm, academic_term_id: e.target.value })}
                  >
                    <option value="">Select a term...</option>
                    {academicTerms.map(term => (
                      <option key={term.id} value={term.id}>{term.academic_year} - Term {term.term}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Standard</label>
                  <select
                    value={masteryForm.standard_id}
                    onChange={(e) => setMasteryForm({ ...masteryForm, standard_id: e.target.value })}
                  >
                    <option value="">Select standard...</option>
                    {standards.map(std => <option key={std.id} value={std.id}>{std.code} — {std.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Mastery Level</label>
                  <select
                    value={masteryForm.mastery_level}
                    onChange={(e) => setMasteryForm({ ...masteryForm, mastery_level: e.target.value })}
                  >
                    {MASTERY_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={masteryForm.notes}
                  onChange={(e) => setMasteryForm({ ...masteryForm, notes: e.target.value })}
                  placeholder="Optional notes on this assessment"
                />
              </div>
              <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="submit" className="btn btn-primary" disabled={savingMastery}>
                  {savingMastery ? 'Saving...' : 'Record Assessment'}
                </button>
              </div>
            </form>

            <div className="filters-row" style={{ marginTop: 24 }}>
              <div className="form-group">
                <label>View records for student</label>
                <select value={masteryStudentFilter} onChange={(e) => setMasteryStudentFilter(e.target.value)}>
                  <option value="">Select student...</option>
                  {masteryStudents.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                  {masteryForm.student_id && !masteryStudents.some(s => s.id === masteryForm.student_id) && (
                    <option value={masteryForm.student_id}>{masteryForm.student_id}</option>
                  )}
                </select>
              </div>
            </div>

            {masteryStudentFilter && (
              loadingMasteryRecords ? (
                <div className="loading">Loading mastery records...</div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Standard</th>
                        <th>Mastery Level</th>
                        <th>Notes</th>
                        <th>Assessed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {masteryRecords.map(record => (
                        <tr key={record.id}>
                          <td>{record.standard_code ? `${record.standard_code} — ${record.standard_title}` : record.standard_id}</td>
                          <td className="capitalize">{record.mastery_level?.replace('_', ' ')}</td>
                          <td>{record.notes || '-'}</td>
                          <td>{new Date(record.assessed_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                      {masteryRecords.length === 0 && (
                        <tr><td colSpan="4" className="empty-state">No mastery records for this student yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}

        {/* Recalled Reports View */}
        {view === 'recalls' && (
          <div className="grades-content">
            <div className="filters-row">
              <div className="form-group">
                <label>Show</label>
                <select value={recallsFilter} onChange={(e) => setRecallsFilter(e.target.value)}>
                  <option value="outstanding">Outstanding (not yet resent)</option>
                  <option value="resolved">Resolved (corrected & resent)</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>

            {loadingRecalls ? (
              <div className="loading">Loading recalled reports...</div>
            ) : recalls.length === 0 ? (
              <div className="empty-state-card">
                <ArrowUUpLeft size={48} weight="duotone" />
                <h3>No recalled reports</h3>
                <p>
                  {recallsFilter === 'outstanding'
                    ? 'Nothing is currently withdrawn for correction.'
                    : recallsFilter === 'resolved'
                    ? 'No recalled reports have been corrected and resent yet.'
                    : 'No report card has ever been recalled.'}
                </p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table grades-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Term</th>
                      <th>Reason</th>
                      <th>Recalled By</th>
                      <th>Recalled At</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recalls.map(r => (
                      <tr key={r.id}>
                        <td className="student-name">{r.student_name}</td>
                        <td>{r.term_label}</td>
                        <td>{r.reason}</td>
                        <td>{r.recalled_by_name || '—'}</td>
                        <td>{new Date(r.recalled_at).toLocaleString()}</td>
                        <td>
                          {r.resent_at ? (
                            <span className="grade-badge" style={{ backgroundColor: '#059669' }}>
                              Corrected & Resent
                            </span>
                          ) : (
                            <span className="grade-badge" style={{ backgroundColor: '#DC2626' }}>
                              Outstanding
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleViewRecalledReport(r)}
                            disabled={downloadingPDF}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Enter Grades Modal */}
        {showGradeModal && (
          <div className="modal-overlay" onClick={() => setShowGradeModal(false)}>
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="enter-grades-modal">
              <div className="modal-header">
                <h2>Enter Grades</h2>
                <button className="modal-close" onClick={() => setShowGradeModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Class</label>
                    <select
                      value={selectedClass}
                      onChange={(e) => setSelectedClass(e.target.value)}
                    >
                      <option value="">Select Class...</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Subject</label>
                    <select
                      value={bulkGradeData.subject_id}
                      onChange={(e) => setBulkGradeData({...bulkGradeData, subject_id: e.target.value})}
                    >
                      <option value="">Select Subject...</option>
                      {subjects.map(subj => (
                        <option key={subj.id} value={subj.id}>{subj.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Assessment Type</label>
                    <select
                      value={bulkGradeData.assessment_type}
                      onChange={(e) => setBulkGradeData({...bulkGradeData, assessment_type: e.target.value})}
                    >
                      {assessmentTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Maximum Score</label>
                    <input
                      type="number"
                      value={bulkGradeData.max_score}
                      onChange={(e) => setBulkGradeData({...bulkGradeData, max_score: e.target.value})}
                      min="1"
                      max="100"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Academic Term</label>
                  <select
                    value={bulkGradeData.academic_term_id}
                    onChange={(e) => setBulkGradeData({...bulkGradeData, academic_term_id: e.target.value})}
                  >
                    <option value="">Select a term...</option>
                    {academicTerms.map(term => (
                      <option key={term.id} value={term.id}>
                        {term.academic_year} - Term {term.term}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedClass && students.length > 0 && (
                  <div className="grade-entry-table">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Student Name</th>
                          <th style={{ width: '120px' }}>Score</th>
                          <th style={{ width: '80px' }}>%</th>
                          <th style={{ width: '60px' }}>Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gradeEntries.map(entry => {
                          const percentage = entry.score ? (parseFloat(entry.score) / parseFloat(bulkGradeData.max_score) * 100) : 0;
                          const gradeInfo = getGradeInfo(percentage);
                          return (
                            <tr key={entry.student_id}>
                              <td>{entry.student_name}</td>
                              <td>
                                <input
                                  type="number"
                                  value={entry.score}
                                  onChange={(e) => updateGradeEntry(entry.student_id, e.target.value)}
                                  min="0"
                                  max={bulkGradeData.max_score}
                                  className="score-input"
                                  placeholder="0"
                                />
                              </td>
                              <td>{entry.score ? `${percentage.toFixed(1)}%` : '-'}</td>
                              <td>
                                {entry.score && (
                                  <span className="grade-badge small" style={{ backgroundColor: gradeInfo.color }}>
                                    {gradeInfo.grade}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowGradeModal(false)}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handleSubmitGrades}
                    data-testid="save-grades-btn"
                  >
                    Save Grades
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Subject Modal */}
        {showSubjectModal && (
          <div className="modal-overlay" onClick={() => setShowSubjectModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} data-testid="add-subject-modal">
              <div className="modal-header">
                <h2>Add Subject</h2>
                <button className="modal-close" onClick={() => setShowSubjectModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateSubject} className="modal-form">
                <div className="form-group">
                  <label>Subject Name</label>
                  <input
                    type="text"
                    value={newSubject.name}
                    onChange={(e) => setNewSubject({...newSubject, name: e.target.value})}
                    placeholder="e.g., Mathematics"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Subject Code</label>
                    <input
                      type="text"
                      value={newSubject.code}
                      onChange={(e) => setNewSubject({...newSubject, code: e.target.value.toUpperCase()})}
                      placeholder="e.g., MATH"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={newSubject.category}
                      onChange={(e) => setNewSubject({...newSubject, category: e.target.value})}
                    >
                      <option value="core">Core</option>
                      <option value="elective">Elective</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Credit Hours</label>
                  <input
                    type="number"
                    value={newSubject.credit_hours}
                    onChange={(e) => setNewSubject({...newSubject, credit_hours: parseInt(e.target.value)})}
                    min="1"
                    max="10"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowSubjectModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">Create Subject</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Download Report Card Modal */}
        {showReportCardModal && (
          <div className="modal-overlay" onClick={() => setShowReportCardModal(false)}>
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="download-reportcard-modal">
              <div className="modal-header">
                <h2>Download Report Card</h2>
                <button className="modal-close" onClick={() => setShowReportCardModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Select Student</label>
                  <select
                    value={reportCardStudent}
                    onChange={(e) => setReportCardStudent(e.target.value)}
                    data-testid="reportcard-student-select"
                  >
                    <option value="">Choose a student...</option>
                    {classGrades && classGrades.students && classGrades.students.map(student => (
                      <option key={student.student_id} value={student.student_id}>
                        {student.student_name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Academic Term</label>
                  <select
                    value={reportCardTerm}
                    onChange={(e) => setReportCardTerm(e.target.value)}
                    data-testid="reportcard-term-select"
                  >
                    <option value="">Select a term...</option>
                    {academicTerms.map(term => (
                      <option key={term.id} value={term.id}>
                        {term.academic_year} - Term {term.term}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="info-box" style={{ marginTop: '15px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '5px', borderLeft: '4px solid #3b82f6' }}>
                  <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>
                    The report card will include all grades recorded for the selected student in the specified academic term.
                  </p>
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowReportCardModal(false)}
                  disabled={downloadingPDF}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handlePreviewReportCard}
                  disabled={downloadingPDF || !reportCardStudent}
                  data-testid="preview-reportcard-btn"
                >
                  {downloadingPDF ? (
                    <>
                      <Spinner size={18} weight="bold" /> Loading...
                    </>
                  ) : (
                    <>
                      <Eye size={18} /> Preview Report Card
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Report Card Modal */}
        {showBulkReportModal && (
          <div className="modal-overlay" onClick={() => setShowBulkReportModal(false)}>
            <div className="modal modal-sm" onClick={e => e.stopPropagation()} data-testid="bulk-reportcard-modal">
              <div className="modal-header">
                <h2>Class Report Cards</h2>
                <button className="modal-close" onClick={() => setShowBulkReportModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Select Class</label>
                  <select
                    value={bulkReportClass}
                    onChange={(e) => setBulkReportClass(e.target.value)}
                    data-testid="bulk-class-select"
                  >
                    <option value="">Choose a class...</option>
                    {classes.map(cls => (
                      <option key={cls.id} value={cls.id}>{cls.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Academic Term</label>
                  <select
                    value={bulkReportTerm}
                    onChange={(e) => setBulkReportTerm(e.target.value)}
                    data-testid="bulk-term-select"
                  >
                    <option value="">Select a term...</option>
                    {academicTerms.map(term => (
                      <option key={term.id} value={term.id}>
                        {term.academic_year} - Term {term.term}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="info-box" style={{ marginTop: '15px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '5px', borderLeft: '4px solid #3b82f6' }}>
                  <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>
                    Generate and preview report cards for all students in the selected class. You can navigate through individual reports and download all at once.
                  </p>
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowBulkReportModal(false)}
                  disabled={bulkDownloadingPDF}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleLoadBulkReports}
                  disabled={bulkDownloadingPDF || !bulkReportClass || !bulkReportTerm}
                  data-testid="load-bulk-reports-btn"
                >
                  {bulkDownloadingPDF ? (
                    <>
                      <Spinner size={18} weight="bold" /> Loading...
                    </>
                  ) : (
                    <>
                      <ChartBar size={18} /> Preview All Reports
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        <ReportCardPreviewModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            if (bulkReportMode) {
              setBulkReportMode(false);
              setBulkReportStudents([]);
              setCurrentReportIndex(0);
            }
          }}
          html={previewHTML}
          studentName={reportCardStudentName}
          academicTerm={reportCardTerm}
          studentId={reportCardStudent}
          onDownloadComplete={() => toast.success('Report card downloaded successfully')}
          bulkMode={bulkReportMode}
          bulkReportStudents={bulkReportStudents}
          currentReportIndex={currentReportIndex}
          totalReports={bulkReportStudents.length}
          onNextReport={goToNextReport}
          onPreviousReport={goToPreviousReport}
          onJumpToStudent={jumpToStudent}
          onDownloadAll={handleDownloadAllReports}
          onApproveAll={handleApproveAllReports}
          status={reportCardStatus}
          canApprove={user?.role === 'super_admin' || user?.role === 'school_admin'}
          onApproved={() => {
            setReportCardStatus('approved');
            if (view === 'recalls') loadRecalls();
          }}
          onRecalled={() => {
            setReportCardStatus('draft');
            if (view === 'recalls') loadRecalls();
          }}
          reportData={reportCardData}
          canEditRemarks={['super_admin', 'school_admin', 'teacher'].includes(user?.role)}
          onRemarksSaved={refreshReportCardPreview}
        />

        {/* Dialog Modal */}
        <DialogModal
          isOpen={dialogConfig.isOpen}
          onClose={closeDialog}
          title={dialogConfig.title}
          message={dialogConfig.message}
          type={dialogConfig.type}
          primaryAction={dialogConfig.primaryAction}
          secondaryAction={dialogConfig.secondaryAction}
        />
      </div>
    </Layout>
  );
}

export default GradesPage;
